// deno-lint-ignore-file
import { useState, useEffect, useLayoutEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useFrameworks } from '../../hooks/useControls.js'
import { motion } from 'framer-motion'
import { ArrowLeft , ArrowRight, Check , X , Pencil , CheckCheck, XCircle, ChevronLeft, ChevronRight, Download } from 'lucide-react'
import API from '../../services/api.js'

// Number of items shown per page in the Clauses / Mappings lists
const PAGE_SIZE = 8

// ============================================================
// HTML export helpers
// ============================================================
function escapeHtml(value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function toTitleCase(status) {
  if (!status) return ''
  return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bAi\b/g, 'AI')
}

// Builds a fully self-contained, scrollable HTML report (inline CSS, no
// external assets) covering the policy header, metrics, every clause, and
// every mapping — independent of whatever page the on-screen pagination is on.
function buildPolicyReportHtml(policy, clauses, mappings) {
  const statusColours = {
    ai_suggested: '#9ca3af',
    accepted: '#4ade80',
    rejected: '#f87171',
    edited: '#60a5fa',
  }

  // ── Metrics (mirrors on-screen MetricsBar) ─────────────────
  const total = mappings.length
  const accepted = mappings.filter(m => m.status === 'accepted').length
  const pending = mappings.filter(m => m.status === 'ai_suggested').length
  const rejected = mappings.filter(m => m.status === 'rejected').length
  const edited = mappings.filter(m => m.status === 'edited').length

  const reviewed = accepted + rejected + edited
  const reviewPercentRaw = total ? (reviewed / total) * 100 : 0
  const reviewPercent = Math.round(reviewPercentRaw * 10) / 10
  const reviewPercentLabel =
    reviewed > 0 && reviewPercent === 0 ? '<0.1%' : `${reviewPercent}%`
  const reviewBarWidth = Math.max(reviewPercentRaw, reviewed > 0 ? 0.5 : 0)

  const metricCards = [
    { label: 'Clauses', value: clauses.length, colour: '#60a5fa' },
    { label: 'Mappings', value: total, colour: '#ffffff' },
    { label: 'Accepted', value: accepted, colour: '#4ade80' },
    { label: 'Pending Review', value: pending, colour: '#facc15' },
    { label: 'Rejected', value: rejected, colour: '#f87171' },
  ]

  const metricsHtml = `
    <div class="metrics-grid">
      ${metricCards.map(m => `
        <div class="metric-card">
          <p class="metric-value" style="color:${m.colour}">${escapeHtml(m.value)}</p>
          <p class="metric-label">${escapeHtml(m.label)}</p>
        </div>
      `).join('')}
    </div>
    <div class="progress-card">
      <div class="progress-head">
        <p class="progress-label">Review Progress</p>
        <p class="progress-value">${reviewed}/${total} (${reviewPercentLabel})</p>
      </div>
      <div class="progress-track">
        <div class="progress-fill" style="width:${reviewBarWidth}%"></div>
      </div>
    </div>
  `

  const clausesHtml = clauses.length
    ? clauses.map(c => `
      <div class="clause">
        <div class="clause-head">
          ${c.clause_number ? `<span class="tag">${escapeHtml(c.clause_number)}</span>` : ''}
          ${c.heading ? `<span class="heading">${escapeHtml(c.heading)}</span>` : ''}
          <span class="meta">${c.page_number ? `p.${escapeHtml(c.page_number)} · ` : ''}#${c.chunk_index + 1}</span>
        </div>
        <p class="clause-content">${escapeHtml(c.content)}</p>
      </div>
    `).join('')
    : '<p class="empty">No clauses extracted.</p>'

  const mappingsHtml = mappings.length
    ? mappings.map(m => {
        const reasoning = m.status === 'edited' ? m.edited_reasoning : m.reasoning
        const colour = statusColours[m.status] || statusColours.ai_suggested
        return `
      <div class="mapping" style="border-left-color:${colour}">
        <div class="mapping-head">
          <span class="tag control">${escapeHtml(m.controls?.control_id)}</span>
          <span class="title">${escapeHtml(m.controls?.title)}</span>
          <span class="badge" style="color:${colour};border-color:${colour}66;background:${colour}1a">
            ${toTitleCase(m.status)}
          </span>
          <span class="confidence">Confidence: ${escapeHtml(m.confidence_score)}%</span>
        </div>
        <div class="mapping-body">
          <div class="block">
            <p class="label">Clause</p>
            <p class="text">${escapeHtml(m.policy_clauses?.content)}</p>
          </div>
          <div class="block">
            <p class="label">Reasoning</p>
            <p class="text">${escapeHtml(reasoning)}</p>
          </div>
          ${m.reviewed_at ? `<p class="reviewed">${toTitleCase(m.status)} · ${escapeHtml(new Date(m.reviewed_at).toLocaleString())}</p>` : ''}
        </div>
      </div>
    `
      }).join('')
    : '<p class="empty">No mappings yet.</p>'

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(policy?.name || 'Policy Report')}</title>
<style>
  :root { color-scheme: dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    background: #0a0a0a;
    color: #d1d5db;
    line-height: 1.6;
  }
  .wrap { max-width: 900px; margin: 0 auto; padding: 40px 24px 80px; }
  header { margin-bottom: 36px; border-bottom: 1px solid #262626; padding-bottom: 20px; }
  h1 { color: #fff; font-size: 26px; font-weight: 600; margin: 0 0 10px; letter-spacing: -0.01em; }
  .meta-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; font-size: 12px; color: #9ca3af; }
  .status-badge {
    padding: 3px 10px; border-radius: 999px; background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1); text-transform: capitalize;
  }
  .generated { font-size: 11px; color: #6b7280; margin-top: 10px; }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
    margin-top: 24px;
  }
  .metric-card {
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 14px;
  }
  .metric-value { font-size: 22px; font-weight: 600; margin: 0; letter-spacing: -0.01em; }
  .metric-label {
    font-size: 10px; color: #6b7280; margin: 4px 0 0;
    text-transform: uppercase; letter-spacing: 0.05em;
  }
  .progress-card {
    margin-top: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 12px;
    padding: 14px 16px;
  }
  .progress-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
  .progress-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin: 0; }
  .progress-value { font-size: 13px; font-weight: 500; color: #fff; margin: 0; }
  .progress-track { width: 100%; height: 8px; border-radius: 999px; background: rgba(255,255,255,0.1); overflow: hidden; }
  .progress-fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(to right, #60a5fa, #4ade80);
  }
  @media (max-width: 640px) {
    .metrics-grid { grid-template-columns: repeat(2, 1fr); }
  }

  h2 { display: flex; align-items: center; gap: 8px; }
  h2 {
    color: #fff; font-size: 15px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.05em; margin: 40px 0 16px;
  }
  h2 .count { color: #6b7280; font-weight: 400; text-transform: none; letter-spacing: normal; }
  .empty { color: #6b7280; font-size: 13px; padding: 16px 0; }

  .jumpnav {
    display: flex; gap: 8px; margin-top: 16px;
  }
  .jumpnav a {
    font-size: 12px; font-weight: 500; color: #d1d5db; text-decoration: none;
    padding: 6px 14px; border-radius: 999px; background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1); transition: background 0.15s;
  }
  .jumpnav a:hover { background: rgba(255,255,255,0.1); }
  section { scroll-margin-top: 20px; }

  .clause {
    padding: 16px; margin-bottom: 10px; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08); border-radius: 12px;
  }
  .clause-head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 8px; }
  .tag {
    font-family: ui-monospace, monospace; font-size: 10px; background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1); color: #9ca3af; padding: 2px 8px; border-radius: 4px;
  }
  .tag.control { color: #60a5fa; }
  .heading { font-size: 13px; font-weight: 500; color: #d1d5db; }
  .meta { font-size: 11px; color: #6b7280; margin-left: auto; }
  .clause-content { font-size: 13px; color: #9ca3af; margin: 0; }

  .mapping {
    padding: 16px; margin-bottom: 12px; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.08); border-left: 3px solid #666;
    border-radius: 12px;
  }
  .mapping-head { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 12px; }
  .title { font-size: 13px; font-weight: 500; color: #fff; }
  .badge { font-size: 10px; font-weight: 500; padding: 2px 9px; border-radius: 999px; border: 1px solid; }
  .confidence { font-size: 11px; color: #9ca3af; margin-left: auto; }
  .block { margin-bottom: 10px; }
  .block:last-child { margin-bottom: 0; }
  .label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; margin: 0 0 4px; }
  .text { font-size: 13px; color: #9ca3af; margin: 0; }
  .reviewed { font-size: 11px; color: #6b7280; margin: 10px 0 0; }

  @media print {
    body { background: #fff; color: #111; }
    .clause, .mapping, .metric-card, .progress-card { background: #fafafa; border-color: #ddd; }
  }
</style>
</head>
<body>
  <div class="wrap">
    <header>
      <h1>${escapeHtml(policy?.name || 'Untitled Policy')}</h1>
      <div class="meta-row">
        <span class="status-badge">${toTitleCase(policy?.status)}</span>
        ${policy?.file_type ? `<span>${escapeHtml(policy.file_type.toUpperCase())}</span>` : ''}
        ${policy?.uploaded_at ? `<span>${escapeHtml(new Date(policy.uploaded_at).toLocaleDateString())}</span>` : ''}
      </div>
      <p class="generated">Report generated ${escapeHtml(new Date().toLocaleString())}</p>
      <nav class="jumpnav">
        <a href="#clauses-section">Clauses (${clauses.length})</a>
        <a href="#mappings-section">Mappings (${mappings.length})</a>
      </nav>
    </header>

    ${metricsHtml}

    <section id="clauses-section">
      <h2>Clauses <span class="count">(${clauses.length})</span></h2>
      ${clausesHtml}
    </section>

    <section id="mappings-section">
      <h2>Mappings <span class="count">(${mappings.length})</span></h2>
      ${mappingsHtml}
    </section>
  </div>
</body>
</html>`
}

function downloadPolicyReport(policy, clauses, mappings) {
  const html = buildPolicyReportHtml(policy, clauses, mappings)
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  const safeName = (policy?.name || 'policy')
    .replace(/[^a-z0-9\-_ ]/gi, '')
    .trim()
    .replace(/\s+/g, '-')
    .toLowerCase() || 'policy'

  const a = document.createElement('a')
  a.href = url
  a.download = `${safeName}-report.html`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ============================================================
// StatusBadge
// ============================================================
function StatusBadge({ status }) {
  const colours = {
    uploaded: 'bg-white/5 text-gray-400 border border-white/10',
    parsing:  'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
    parsed:   'bg-blue-500/10 text-blue-400 border border-blue-500/20',
    mapping:  'bg-purple-500/10 text-purple-400 border border-purple-500/20',
    mapped:   'bg-green-500/10 text-green-400 border border-green-500/20',
    failed:   'bg-red-500/10 text-red-400 border border-red-500/20',
  }

  return (
    <span className={`
      px-2 sm:px-3 py-1 rounded-full text-[10px] sm:text-xs
      font-medium inline-flex items-center gap-2
      ${colours[status] || 'bg-white/5 text-gray-400 border border-white/10'}
    `}>
      {status === 'parsing' && (
        <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
      )}
      {status === 'mapping' && (
        <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
      )}
      {status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bAi\b/g, 'AI')}
    </span>
  )
}

// ============================================================
// Pagination
// ============================================================
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null

  // Build a compact page-number list: first, last, current +/-1, with ellipses
  const pages = []
  for (let p = 1; p <= totalPages; p++) {
    if (
      p === 1 ||
      p === totalPages ||
      (p >= page - 1 && p <= page + 1)
    ) {
      pages.push(p)
    } else if (pages[pages.length - 1] !== '…') {
      pages.push('…')
    }
  }

  return (
    <div className="flex items-center justify-center gap-1 mt-4 pt-2">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className="
          w-7 h-7 flex items-center justify-center rounded-md
          text-gray-400 hover:text-white hover:bg-white/10
          disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed
          transition
        "
        aria-label="Previous page"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`ellipsis-${i}`} className="px-1.5 text-xs text-gray-600">…</span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={`
              min-w-[28px] h-7 px-1.5 rounded-md text-[11px] font-medium transition
              ${p === page
                ? 'bg-white/10 border border-white/20 text-white'
                : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'}
            `}
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className="
          w-7 h-7 flex items-center justify-center rounded-md
          text-gray-400 hover:text-white hover:bg-white/10
          disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed
          transition
        "
        aria-label="Next page"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  )
}

// ============================================================
// MetricsBar
// ============================================================
function MetricsBar({ clauses, mappings }) {
  const total = mappings.length
  const accepted = mappings.filter(m => m.status === 'accepted').length
  const pending = mappings.filter(m => m.status === 'ai_suggested').length
  const rejected = mappings.filter(m => m.status === 'rejected').length
  const edited = mappings.filter(m => m.status === 'edited').length

  const reviewed = accepted + rejected + edited
  const reviewPercentRaw = total ? (reviewed / total) * 100 : 0
  const reviewPercent = Math.round(reviewPercentRaw * 10) / 10 // 1 decimal place

  const reviewPercentLabel =
    reviewed > 0 && reviewPercent === 0 ? '<0.1%' : `${reviewPercent}%`

  const metrics = [
    { label: 'Clauses',        value: clauses.length, color: 'text-blue-400' },
    { label: 'Mappings',       value: total,           color: 'text-white' },
    { label: 'Accepted',       value: accepted,        color: 'text-green-400' },
    { label: 'Pending Review', value: pending,         color: 'text-yellow-400' },
    { label: 'Rejected',       value: rejected,        color: 'text-red-400' },
  ]

  return (
    <div className="mb-5 sm:mb-6">
      <div className="
        grid grid-cols-5
        gap-2 sm:gap-3
      ">
        {metrics.map(m => (
          <div
            key={m.label}
            className="
              bg-white/5 backdrop-blur-2xl
              border border-white/10
              rounded-xl
              px-3 py-3 sm:py-4
            "
          >
            <p className={`text-lg sm:text-2xl font-semibold tracking-tight ${m.color}`}>
              {m.value}
            </p>
            <p className="text-[10px] sm:text-[11px] text-gray-500 mt-1 uppercase tracking-wide">
              {m.label}
            </p>
          </div>
        ))}
      </div>

      {/* Review progress bar */}
      <div className="
        mt-3 sm:mt-4
        bg-white/5 backdrop-blur-2xl
        border border-white/10
        rounded-xl
        px-4 py-3
      ">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] sm:text-[11px] text-gray-500 uppercase tracking-wide">
            Review Progress
          </p>
          <p className="text-xs sm:text-sm font-medium text-white">
            {reviewed}/{total} ({reviewPercentLabel})
          </p>
        </div>
        <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-blue-400 to-green-400 transition-all duration-300"
            style={{ width: `${Math.max(reviewPercentRaw, reviewed > 0 ? 0.5 : 0)}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Framework Selector Modal
// ============================================================
function FrameworkSelectorModal({ onConfirm, onClose }) {
  const { frameworks, loading, error: frameworksError, refetch } = useFrameworks()
  const [selected, setSelected] = useState([])
  const [error, setError] = useState(null)

  function toggleFramework(id) {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    )
  }

  function handleConfirm() {
    if (selected.length === 0) {
      setError('Please select at least one framework')
      return
    }
    onConfirm(selected)
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="
          bg-black/60 backdrop-blur-2xl
          border border-white/10
          rounded-xl sm:rounded-2xl
          p-5 sm:p-6
          w-full max-w-md max-h-[85vh] overflow-y-auto
          shadow-2xl
        "
      >
        <h2 className="text-base sm:text-lg font-semibold text-white mb-1 tracking-tight">
          Select Frameworks
        </h2>
        <p className="text-xs sm:text-sm text-gray-400 mb-4">
          Choose which frameworks to map this policy against.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500 py-4 text-center">Loading frameworks...</p>
        ) : frameworksError ? (
          <div role="alert" className="py-4 text-center">
            <p className="text-sm text-red-400">Could not load frameworks: {frameworksError}</p>
            <button type="button" onClick={refetch} className="mt-3 text-xs text-blue-400 hover:text-blue-300">
              Retry
            </button>
          </div>
        ) : frameworks.length === 0 ? (
          <p className="text-sm text-red-400 py-4 text-center">
            No frameworks are available in the controls library.
          </p>
        ) : (
          <div className="space-y-2 mb-4">
            {frameworks.map(framework => (
              <label
                key={framework.id}
                className="
                  flex items-start gap-3 p-3
                  rounded-lg border border-white/10
                  hover:border-white/20 hover:bg-white/5
                  cursor-pointer transition
                "
              >
                <input
                  type="checkbox"
                  checked={selected.includes(framework.id)}
                  onChange={() => toggleFramework(framework.id)}
                  className="mt-0.5 accent-blue-500"
                />
                <div>
                  <p className="text-sm font-medium text-white">{framework.name}</p>
                  <p className="text-xs text-gray-500">
                    {framework.version && `v${framework.version} · `}
                    {framework.is_custom ? 'Custom framework' : 'Global standard'}
                  </p>
                  {framework.description && (
                    <p className="text-xs text-gray-600 mt-0.5">{framework.description}</p>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-400 mb-3">{error}</p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            type='button'
            onClick={onClose}
            className="px-4 py-2 text-xs sm:text-sm text-gray-400 hover:text-white transition"
          >
            Cancel
          </button>
          <motion.button
            onClick={handleConfirm}
            disabled={selected.length === 0}
            whileHover={selected.length !== 0 ? { scale: 1.03 } : {}}
            whileTap={selected.length !== 0 ? { scale: 0.97 } : {}}
            transition={{ type: "spring", stiffness: 110, damping: 15, mass: 0.8 }}
            className="
              px-4 py-2 rounded-lg
              text-[11px] sm:text-sm font-medium
              text-white bg-blue-500/20 border border-blue-500/30
              hover:bg-blue-500/30 hover:border-blue-400/50
              disabled:opacity-40 disabled:cursor-not-allowed
              focus:outline-none transition-colors duration-200
            "
          >
            Start Mapping ({selected.length} selected)
          </motion.button>
        </div>
      </motion.div>
    </div>
  )
}

// ============================================================
// Main PolicyDetail Component
// ============================================================
export default function PolicyDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [policy, setPolicy] = useState(null)
  const [clauses, setClauses] = useState([])
  const [mappings, setMappings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showFrameworkModal, setShowFrameworkModal] = useState(false)
  const [mapping, setMapping] = useState(false)
  const [mappingError, setMappingError] = useState(null)
  const [selectedMappings, setSelectedMappings] = useState([])
  const [bulkProcessing, setBulkProcessing] = useState(false)
  const [activeSection, setActiveSection] = useState('clauses')
  const [clausesPage, setClausesPage] = useState(1)
  const [mappingsPage, setMappingsPage] = useState(1)
  const [downloading, setDownloading] = useState(false)

  // Asset scope — same sessionStorage → /resolve-asset-id/ pattern as
  // Policies.jsx and Controls.jsx. Every Django GRC endpoint requires
  // asset_id (see rcm/auth_utils.py get_current_profile), so nothing
  // below can fetch until this resolves.
  const [assetId, setAssetId] = useState(null)
  const [resolvingAsset, setResolvingAsset] = useState(true)
  const selectedAppName = sessionStorage.getItem('selectedApp')

  useEffect(() => {
    if (!selectedAppName) {
      setError('No asset selected — navigate to this page via an asset.')
      setResolvingAsset(false)
      return
    }
    setResolvingAsset(true)
    API.get('/resolve-asset-id/', { params: { name: selectedAppName } })
      .then(({ data }) => {
        setAssetId(data.id)
        setError(null)
      })
      .catch(() => {
        setError('Could not resolve the selected asset. Please re-select it from the dashboard.')
      })
      .finally(() => setResolvingAsset(false))
  }, [selectedAppName])

  // ── Fetch functions ──
  // Retries indefinitely on a fixed interval rather than giving up after a
  // couple of attempts. Transient backend/DB-connection blips (DNS
  // resolution hiccups, pooler reconnects, etc.) have been observed taking
  // up to ~20s to self-resolve, so a short exponential backoff isn't
  // enough — this keeps trying every 3s until it succeeds or maxWaitMs is
  // exceeded, so the page can self-heal without a manual refresh.
  // 4xx errors (auth/permissions/404) are not transient and fail immediately.
  async function withRetry(fn, { intervalMs = 3000, maxWaitMs = 60000 } = {}) {
    const start = Date.now()
    let lastErr
    while (Date.now() - start < maxWaitMs) {
      try {
        return await fn()
      } catch (err) {
        lastErr = err
        const httpStatus = err?.response?.status
        const isClientError = httpStatus >= 400 && httpStatus < 500
        if (isClientError) throw err
        await new Promise(res => setTimeout(res, intervalMs))
      }
    }
    throw lastErr
  }

  const fetchPolicy = useCallback(async () => {
    if (!assetId) return
    try {
      const { data } = await withRetry(() =>
        API.get(`/policies/${id}/`, { params: { asset_id: assetId } })
      )
      setPolicy(data)
      setError(null)
    } catch (err) {
      setError(err?.response?.data?.detail || err.message)
    }
    setLoading(false)
  }, [id, assetId])

  const fetchClauses = useCallback(async () => {
    if (!assetId) return
    try {
      const { data } = await withRetry(() =>
        API.get(`/policies/${id}/clauses/`, { params: { asset_id: assetId } })
      )
      setClauses(data || [])
    } catch (err) {
      console.error('fetchClauses failed:', err)
    }
  }, [id, assetId])

  const fetchMappings = useCallback(async () => {
    if (!assetId) return
    try {
      const { data } = await withRetry(() =>
        API.get(`/policies/${id}/mappings/`, { params: { asset_id: assetId } })
      )
      setMappings(data || [])
    } catch (err) {
      console.error('fetchMappings failed:', err)
    }
  }, [id, assetId])

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  // Initial load — waits for assetId to resolve first
  useEffect(() => {
    if (!assetId) return
    fetchPolicy()
    fetchClauses()
    fetchMappings()
  }, [id, assetId, fetchPolicy, fetchClauses, fetchMappings])

  // Poll while the policy is in an in-progress state, so status updates
  // (uploaded -> parsing -> parsed -> mapping -> mapped) show up live
  // without a manual refresh.
  useEffect(() => {
    if (!['parsing', 'uploaded', 'mapping'].includes(policy?.status)) return
    const interval = setInterval(() => {
      fetchPolicy()
      if (policy.status === 'parsing') fetchClauses()
      if (policy.status === 'mapping') fetchMappings()
    }, 4000)
    return () => clearInterval(interval)
  }, [policy?.status, fetchPolicy, fetchClauses, fetchMappings])

  // Clear the local "mapping" spinner once the policy flips to mapped
  useEffect(() => {
    if (policy?.status === 'mapped') setMapping(false)
  }, [policy?.status])

  // Reset pagination whenever the underlying data set is (re)loaded for a new policy
  useEffect(() => {
    setClausesPage(1)
    setMappingsPage(1)
  }, [id])

  async function handleStartMapping(frameworkIds) {
    setShowFrameworkModal(false)
    setMapping(true)
    setMappingError(null)
    try {
      await API.post(`/policies/${id}/start-mapping/`, {
        framework_ids: frameworkIds,
        asset_id: assetId,
      })
      await fetchPolicy()
    } catch (err) {
      setMappingError(err?.response?.data?.detail || err.message)
      setMapping(false)
    }
  }

  async function handleReview(mappingId, status, editedReasoning = null) {
    const previous = mappings
    setMappings(prev => prev.map(m =>
      m.id === mappingId
        ? {
            ...m,
            status,
            edited_reasoning: status === 'edited' ? editedReasoning : m.edited_reasoning,
            reviewed_at: new Date().toISOString(),
          }
        : m
    ))

    try {
      await API.patch(`/policies/mappings/${mappingId}/review/`, {
        status,
        edited_reasoning: editedReasoning,
        asset_id: assetId,
      })
    } catch (err) {
      console.error('Review failed:', err)
      setMappings(previous)
      setMappingError('Failed to update mapping status.')
    }
  }

  function handleSelectMapping(mappingId, checked) {
    setSelectedMappings(prev =>
      checked ? [...prev, mappingId] : prev.filter(id => id !== mappingId)
    )
  }

  function handleSelectAllPending() {
    if (selectedMappings.length === pendingMappings.length) setSelectedMappings([])
    else setSelectedMappings(pendingMappings.map(m => m.id))
  }

  async function handleBulkAccept() {
    setBulkProcessing(true)
    const eligibleIds = selectedMappings.filter(id => {
      const m = mappings.find(m => m.id === id)
      return m && m.confidence_score >= 80
    })

    const previous = mappings
    const now = new Date().toISOString()
    setMappings(prev => prev.map(m =>
      eligibleIds.includes(m.id) ? { ...m, status: 'accepted', reviewed_at: now } : m
    ))

    const results = await Promise.allSettled(
      eligibleIds.map(id =>
        API.patch(`/policies/mappings/${id}/review/`, { status: 'accepted', asset_id: assetId })
      )
    )

    const failedIds = eligibleIds.filter((_, i) => results[i].status === 'rejected')
    if (failedIds.length > 0) {
      setMappings(prev => prev.map(m => {
        if (!failedIds.includes(m.id)) return m
        const original = previous.find(p => p.id === m.id)
        return original || m
      }))
      setMappingError(`${failedIds.length} mapping(s) failed to update.`)
    }

    const skipped = selectedMappings.length - eligibleIds.length
    if (skipped > 0) {
      alert(`${eligibleIds.length - failedIds.length} mappings accepted. ${skipped} skipped — confidence below 80%.`)
    }

    setSelectedMappings([])
    setBulkProcessing(false)
  }

  async function handleBulkReject() {
    setBulkProcessing(true)

    const previous = mappings
    const now = new Date().toISOString()
    const targetIds = selectedMappings
    setMappings(prev => prev.map(m =>
      targetIds.includes(m.id) ? { ...m, status: 'rejected', reviewed_at: now } : m
    ))

    const results = await Promise.allSettled(
      targetIds.map(id =>
        API.patch(`/policies/mappings/${id}/review/`, { status: 'rejected', asset_id: assetId })
      )
    )

    const failedIds = targetIds.filter((_, i) => results[i].status === 'rejected')
    if (failedIds.length > 0) {
      setMappings(prev => prev.map(m => {
        if (!failedIds.includes(m.id)) return m
        const original = previous.find(p => p.id === m.id)
        return original || m
      }))
      setMappingError(`${failedIds.length} mapping(s) failed to update.`)
    }

    setSelectedMappings([])
    setBulkProcessing(false)
  }

  function handleDownload() {
    setDownloading(true)
    try {
      downloadPolicyReport(policy, clauses, mappings)
    } finally {
      setDownloading(false)
    }
  }

  if (loading || resolvingAsset) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </div>
    )
  }

  const pendingMappings = mappings.filter(m => m.status === 'ai_suggested')

  // ── Pagination slices ───────────────────────────────────────
  const totalClausesPages = Math.max(1, Math.ceil(clauses.length / PAGE_SIZE))
  const clausesPageSafe = Math.min(clausesPage, totalClausesPages)
  const paginatedClauses = clauses.slice(
    (clausesPageSafe - 1) * PAGE_SIZE,
    clausesPageSafe * PAGE_SIZE
  )

  const totalMappingsPages = Math.max(1, Math.ceil(mappings.length / PAGE_SIZE))
  const mappingsPageSafe = Math.min(mappingsPage, totalMappingsPages)
  const paginatedMappings = mappings.slice(
    (mappingsPageSafe - 1) * PAGE_SIZE,
    mappingsPageSafe * PAGE_SIZE
  )

  return (
    <div className="relative min-h-screen overflow-hidden">

      <div className="absolute inset-0 bg-cover bg-center" />
      <div className="absolute inset-0 bg-black/80" />

      {showFrameworkModal && (
        <FrameworkSelectorModal
          onConfirm={handleStartMapping}
          onClose={() => setShowFrameworkModal(false)}
        />
      )}

      <div className="
        relative z-10
        w-full max-w-[1400px] mx-auto
        px-3 md:px-4 lg:px-6
        py-4 sm:py-8
        min-w-0
      ">

        {/* ── HEADER ── */}
        <div className="mb-3 sm:mb-5">

          <div className="flex items-center justify-between gap-4">

            <div className="flex items-center gap-3 min-w-0">
              <motion.button
                onClick={() => navigate('/grc/policies')}
                className="text-gray-400 hover:text-white transition-colors"
                whileHover={{ scale: 1.08, x: -2 }}
                whileTap={{ scale: 0.92 }}
              >
                <ArrowLeft className="w-5 h-5" />
              </motion.button>

              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl lg:text-3xl font-semibold text-white tracking-tight break-words">
                  {policy?.name}
                </h1>
                <div className="mt-1 h-[2px] w-16 sm:w-32 lg:w-44 bg-gradient-to-r from-blue-500/60 via-blue-400/10 to-transparent animate-pulse" />
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <motion.button
                onClick={handleDownload}
                disabled={downloading || loading}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="
                  px-3 sm:px-4 py-2 rounded-lg
                  text-[11px] sm:text-sm font-medium
                  text-gray-300 bg-white/5 border border-white/10
                  hover:bg-white/10 hover:border-white/20
                  disabled:opacity-40 disabled:cursor-not-allowed
                  flex items-center gap-2
                "
                title="Download a shareable HTML report with all clauses and mappings"
              >
                <Download className="w-4 h-4" />
                Download HTML
              </motion.button>

              <motion.button
                onClick={() => setShowFrameworkModal(true)}
                disabled={policy?.status !== 'parsed' || mapping}
                className="
                  px-3 sm:px-4 py-2 rounded-lg
                  text-[11px] sm:text-sm font-medium
                  text-gray-300 bg-white/5 border border-white/10
                  hover:bg-white/10 hover:border-white/20
                  disabled:opacity-40 disabled:cursor-not-allowed
                  flex items-center gap-2
                "
              >
                {mapping ? (
                  <>
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                    Mapping...
                  </>
                ) : (
                  <>
                    Start Mapping
                    <ArrowRight className="w-4 h-4 mt-[2px]" />
                  </>
                )}
              </motion.button>
            </div>

          </div>

          <div className="flex items-start gap-3 mt-3 pl-[30px] sm:pl-[30px]">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <StatusBadge status={policy?.status} />
              <span className="text-[11px] text-gray-400 uppercase">
                {policy?.file_type}
              </span>
              <span className="text-[11px] text-gray-600">
                {new Date(policy?.uploaded_at).toLocaleDateString()}
              </span>
            </div>
          </div>

        </div>

        {/* ── METRICS ── */}
        <MetricsBar clauses={clauses} mappings={mappings} />

        {/* ── STATUS BANNERS ── */}
        {mappingError && (
          <div className="
            mb-5 sm:mb-6
            bg-red-500/10 border border-red-500/20
            text-red-400
            px-3 sm:px-4 py-3
            rounded-lg text-xs sm:text-sm
            flex items-center gap-3
          ">
            <span className="w-2 h-2 bg-red-400 rounded-full shrink-0" />
            {mappingError}
          </div>
        )}

        {(policy?.status === 'mapping' || mapping) && (
          <div className="
            mb-5 sm:mb-6
            bg-purple-500/10 border border-purple-500/20
            text-purple-400
            px-3 sm:px-4 py-3
            rounded-lg text-xs sm:text-sm
            flex items-center gap-3
          ">
            <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse shrink-0" />
            AI is mapping clauses to controls. This may take a few minutes...
          </div>
        )}

        {(policy?.status === 'parsing' || policy?.status === 'uploaded') && (
          <div className="
            mb-5 sm:mb-6
            bg-yellow-500/10 border border-yellow-500/20
            text-yellow-400
            px-3 sm:px-4 py-3
            rounded-lg text-xs sm:text-sm
            flex items-center gap-3
          ">
            <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse shrink-0" />
            Document is being parsed into clauses...
          </div>
        )}

        {/* ── SLIDING TABS ── */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="
            flex w-fit
            bg-white/[0.02]
            border border-white/10
            rounded-lg p-1 mb-4
          ">
            {[
              { id: 'clauses',  label: 'Clauses',  count: clauses.length },
              { id: 'mappings', label: 'Mappings', count: mappings.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className="relative px-5 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors"
              >
                {activeSection === tab.id && (
                  <motion.div
                    layoutId="section-pill"
                    className="absolute inset-0 bg-white/10 border border-white/10 rounded-md"
                    transition={{ type: "spring", stiffness: 450, damping: 35 }}
                  />
                )}
                <span className={`
                  relative z-10 flex items-center gap-1
                  ${activeSection === tab.id ? 'text-white' : 'text-gray-500 hover:text-gray-300'}
                `}>
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`text-[10px] ${activeSection === tab.id ? 'text-gray-400' : 'text-gray-600'}`}>
                      {tab.count}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>

          <motion.div
            key={activeSection}
            initial={{ opacity: 0, x: activeSection === 'clauses' ? -12 : 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >

            {/* ── MAPPINGS PANEL ── */}
            {activeSection === 'mappings' && (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <button
                    onClick={handleSelectAllPending}
                    className="text-xs text-gray-400 hover:text-white transition"
                  >
                    {selectedMappings.length === pendingMappings.length ? 'Deselect All' : 'Select All'}
                  </button>

                  {selectedMappings.length > 0 && (
                    <>
                      <span className="text-xs text-gray-500">{selectedMappings.length} selected</span>

                      <button
                        onClick={handleBulkAccept}
                        disabled={bulkProcessing}
                        className="
                          px-3 py-1.5 rounded-md
                          bg-green-500/10 text-green-400 border border-green-500/20
                          hover:bg-green-500/20 transition
                          flex items-center gap-1.5
                        "
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        Accept
                      </button>

                      <button
                        onClick={handleBulkReject}
                        disabled={bulkProcessing}
                        className="
                          px-3 py-1.5 rounded-md
                          bg-red-500/10 text-red-400 border border-red-500/20
                          hover:bg-red-500/20 transition
                          flex items-center gap-1.5
                        "
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        Reject
                      </button>
                    </>
                  )}

                  {mappings.length > 0 && (
                    <span className="text-[11px] text-gray-600 ml-auto">
                      Showing {(mappingsPageSafe - 1) * PAGE_SIZE + 1}
                      –{Math.min(mappingsPageSafe * PAGE_SIZE, mappings.length)} of {mappings.length}
                    </span>
                  )}
                </div>

                {mappings.length === 0 ? (
                  <div className="
                    bg-white/5 backdrop-blur-2xl border border-white/10
                    rounded-xl sm:rounded-2xl
                    text-center py-14 sm:py-16 px-4
                  ">
                    <p className="text-base sm:text-lg text-gray-300">No mappings yet</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">
                      Start mapping to see results here
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="
                      bg-white/5 backdrop-blur-2xl
                      border border-white/10
                      rounded-xl sm:rounded-2xl
                      overflow-hidden
                      divide-y divide-white/5
                    ">
                      {paginatedMappings.map(m => (
                        <MappingCard
                          key={m.id}
                          mapping={m}
                          assetId={assetId}
                          onReview={handleReview}
                          selected={selectedMappings.includes(m.id)}
                          onSelect={handleSelectMapping}
                        />
                      ))}
                    </div>
                    <Pagination
                      page={mappingsPageSafe}
                      totalPages={totalMappingsPages}
                      onChange={setMappingsPage}
                    />
                  </>
                )}
              </>
            )}

            {/* ── CLAUSES PANEL ── */}
            {activeSection === 'clauses' && (
              <>
                {clauses.length === 0 && policy?.status === 'parsed' ? (
                  <div className="
                    bg-white/5 backdrop-blur-2xl border border-white/10
                    rounded-xl sm:rounded-2xl
                    text-center py-14 sm:py-16 px-4
                  ">
                    <p className="text-base sm:text-lg text-gray-300">No clauses extracted</p>
                    <p className="text-xs sm:text-sm text-gray-500 mt-2">
                      No clauses were found in this document.
                    </p>
                  </div>
                ) : (
                  <>
                    {clauses.length > 0 && (
                      <div className="flex justify-end mb-2">
                        <span className="text-[11px] text-gray-600">
                          Showing {(clausesPageSafe - 1) * PAGE_SIZE + 1}
                          –{Math.min(clausesPageSafe * PAGE_SIZE, clauses.length)} of {clauses.length}
                        </span>
                      </div>
                    )}
                    <div className="
                      bg-white/5 backdrop-blur-2xl
                      border border-white/10
                      rounded-xl sm:rounded-2xl
                      overflow-hidden
                      divide-y divide-white/5
                    ">
                      {paginatedClauses.map(clause => (
                        <div
                          key={clause.id}
                          className="px-3 sm:px-5 py-4 hover:bg-white/5 transition"
                        >
                          <div className="
                            flex flex-col sm:flex-row
                            sm:justify-between sm:items-start
                            gap-2 mb-2
                          ">
                            <div className="flex flex-wrap items-center gap-1.5 leading-tight">
                              {clause.clause_number && (
                                <span className="
                                  text-[10px] font-mono
                                  bg-white/5 border border-white/10
                                  text-gray-400 px-2 py-0.5 rounded
                                ">
                                  {clause.clause_number}
                                </span>
                              )}
                              {clause.heading && (
                                <span className="text-sm font-medium text-gray-300">
                                  {clause.heading}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] text-gray-600 shrink-0">
                              {clause.page_number && <span>p.{clause.page_number}</span>}
                              <span>#{clause.chunk_index + 1}</span>
                            </div>
                          </div>
                          <p className="text-xs sm:text-sm text-gray-400 leading-relaxed break-words">
                            {clause.content}
                          </p>
                        </div>
                      ))}
                    </div>
                    <Pagination
                      page={clausesPageSafe}
                      totalPages={totalClausesPages}
                      onChange={setClausesPage}
                    />
                  </>
                )}
              </>
            )}

          </motion.div>
        </motion.div>

      </div>
    </div>
  )
}

// ============================================================
// MappingCard
// ============================================================
function MappingCard({ mapping, assetId, onReview, selected = false, onSelect }) {
  const [editing, setEditing] = useState(false)
  const [editedReasoning, setEditedReasoning] = useState(mapping.reasoning)
  const [activeTab, setActiveTab] = useState('review')
  const [history, setHistory] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [comments, setComments] = useState([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)

  async function fetchHistory() {
    setHistoryLoading(true)
    try {
      const { data } = await API.get(`/policies/mappings/${mapping.id}/history/`, { params: { asset_id: assetId } })
      setHistory(data)
    } catch (err) { console.error('Mapping history unavailable:', err?.response?.status) }
    finally { setHistoryLoading(false) }
  }

  async function fetchComments() {
    setCommentsLoading(true)
    try {
      const { data } = await API.get(`/policies/mappings/${mapping.id}/comments/`, { params: { asset_id: assetId } })
      setComments(data)
    } catch (err) { console.error('Mapping comments unavailable:', err?.response?.status) }
    finally { setCommentsLoading(false) }
  }

  async function submitComment() {
    if (!newComment.trim()) return
    setSubmittingComment(true)
    try {
      await API.post(`/policies/mappings/${mapping.id}/comments/`, { asset_id: assetId, comment: newComment.trim() })
      setNewComment('')
      await fetchComments()
    } catch (err) { console.error('Comment could not be saved:', err?.response?.status) }
    finally { setSubmittingComment(false) }
  }

  function handleTabChange(tab) {
    setActiveTab(tab)
    if (tab === 'history' && history.length === 0) fetchHistory()
    if (tab === 'comments' && comments.length === 0) fetchComments()
  }

  function getConfidenceColor(score) {
    if (score >= 80) return 'bg-green-500'
    if (score >= 60) return 'bg-yellow-500'
    return 'bg-red-500'
  }

  function getMappingStatusBadge(status) {
    const styles = {
      ai_suggested: 'bg-white/5 text-gray-400 border border-white/10',
      accepted:     'bg-green-500/10 text-green-400 border border-green-500/20',
      rejected:     'bg-red-500/10 text-red-400 border border-red-500/20',
      edited:       'bg-blue-500/10 text-blue-400 border border-blue-500/20',
    }
    const label = status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bAi\b/g, 'AI')
    return (
      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${styles[status] || styles.ai_suggested}`}>
        {label}
      </span>
    )
  }

  return (
    <div className={`
      px-3 sm:px-5 py-4
      border-b border-white/5
      transition-colors duration-200
      hover:bg-white/5
      ${mapping.status === 'accepted'
        ? 'border-l-2 border-l-green-500/40'
        : mapping.status === 'rejected'
        ? 'border-l-2 border-l-red-500/30'
        : mapping.status === 'edited'
        ? 'border-l-2 border-l-blue-500/40'
        : 'border-l-2 border-l-transparent'
      }
      ${mapping.status === 'rejected' ? 'opacity-60' : ''}
    `}>

      {/* Card header */}
      <div className="
        flex flex-col lg:flex-row lg:justify-between lg:items-start
        gap-3 mb-3
      ">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {mapping.status === 'ai_suggested' && onSelect && (
            <input
              type="checkbox"
              checked={selected}
              onChange={e => onSelect(mapping.id, e.target.checked)}
              className="accent-blue-500 cursor-pointer shrink-0"
              onClick={e => e.stopPropagation()}
            />
          )}
          <span className="
            text-[10px] font-mono
            bg-white/5 border border-white/10
            text-blue-400 px-2 py-0.5 rounded
          ">
            {mapping.controls?.control_id}
          </span>
          <span className="text-xs sm:text-sm font-medium text-white break-words">
            {mapping.controls?.title}
          </span>
          {getMappingStatusBadge(mapping.status)}
        </div>

        {/* Confidence bar */}
        <div className="flex items-center gap-2 w-full lg:w-auto lg:justify-end">
          <span className="text-[11px] sm:text-[12px] text-gray-400 whitespace-nowrap">
            Confidence Score: {mapping.confidence_score}%
          </span>
          <div className="flex-1 lg:flex-none lg:w-20 h-1.5 bg-white/10 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${getConfidenceColor(mapping.confidence_score)}`}
              style={{ width: `${mapping.confidence_score}%` }}
            />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-3 border-b border-white/5">
        {['review', 'history', 'comments'].map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`
              px-3 py-1.5 text-[11px] sm:text-xs font-medium transition
              border-b-2 -mb-px capitalize
              ${activeTab === tab
                ? 'border-blue-500 text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-300'}
            `}
          >
            {tab}{tab === 'comments' && comments.length > 0 ? ` (${comments.length})` : ''}
          </button>
        ))}
      </div>

      {/* ── Review Tab ── */}
      {activeTab === 'review' && (
        <>
          <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
            <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Clause</p>
            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
              {mapping.policy_clauses?.content}
            </p>
          </div>

          {editing ? (
            <div className="mb-3">
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Edit reasoning</p>
              <textarea
                value={editedReasoning}
                onChange={e => setEditedReasoning(e.target.value)}
                className="
                  w-full bg-white/5 border border-white/10 rounded-lg
                  px-3 py-2 text-xs sm:text-sm text-gray-300
                  focus:outline-none focus:border-blue-500/50 focus:bg-white/10
                  resize-none transition
                "
                rows={3}
              />
            </div>
          ) : (
            <div className="mb-3">
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wide">Reasoning</p>
              <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">
                {mapping.status === 'edited' ? mapping.edited_reasoning : mapping.reasoning}
              </p>
            </div>
          )}

          {['ai_suggested', 'edited'].includes(mapping.status) && !editing && (
            <div className="flex gap-2">
              <motion.button
                onClick={() => onReview(mapping.id, 'accepted')}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }}
                className="
                  flex-1 px-3 py-1.5 rounded-md
                  text-[11px] sm:text-xs font-medium
                  bg-green-500/10 text-green-400 border border-green-500/20
                  hover:bg-green-500/20 transition-colors duration-200
                  flex items-center justify-center gap-1.5
                "
              >
                <Check className="w-3.5 h-3.5" />
                {mapping.status === 'edited' ? 'Accept Edit' : 'Accept'}
              </motion.button>

              <motion.button
                onClick={() => onReview(mapping.id, 'rejected')}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }}
                className="
                  flex-1 px-3 py-1.5 rounded-md
                  text-[11px] sm:text-xs font-medium
                  bg-red-500/10 text-red-400 border border-red-500/20
                  hover:bg-red-500/20 transition-colors duration-200
                  flex items-center justify-center gap-1.5
                "
              >
                <X className="w-3.5 h-3.5" />
                Reject
              </motion.button>

              <motion.button
                onClick={() => setEditing(true)}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 110, damping: 15, mass: 0.8 }}
                className="
                  flex-1 px-3 py-1.5 rounded-md
                  text-[11px] sm:text-xs font-medium
                  bg-white/5 text-gray-400 border border-white/10
                  hover:bg-white/10 hover:text-white
                  transition-colors duration-200
                  flex items-center justify-center gap-1.5
                "
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </motion.button>
            </div>
          )}

          {editing && (
            <div className="flex gap-2">
              <button
                onClick={() => { onReview(mapping.id, 'edited', editedReasoning); setEditing(false) }}
                className="
                  px-3 py-1.5 rounded-md
                  text-[11px] sm:text-xs font-medium
                  bg-blue-500/10 text-blue-400 border border-blue-500/20
                  hover:bg-blue-500/20 transition
                "
              >
                Save Edit
              </button>
              <button
                onClick={() => { setEditing(false); setEditedReasoning(mapping.reasoning) }}
                className="
                  px-3 py-1.5 rounded-md
                  text-[11px] sm:text-xs font-medium
                  bg-white/5 text-gray-400 border border-white/10
                  hover:bg-white/10 transition
                "
              >
                Cancel
              </button>
            </div>
          )}

          {mapping.status !== 'ai_suggested' && (
            <p className="text-[11px] text-gray-600 mt-2">
              {mapping.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} · {new Date(mapping.reviewed_at).toLocaleString()}
            </p>
          )}
        </>
      )}

      {/* ── History Tab ── */}
      {activeTab === 'history' && (
        <div>
          {historyLoading ? (
            <p className="text-xs text-gray-500 py-4 text-center">Loading history...</p>
          ) : history.length === 0 ? (
            <p className="text-xs text-gray-600 py-4 text-center">No history yet.</p>
          ) : (
            <div className="space-y-2">
              {history.map(entry => {
                const oldStatus = entry.old_data?.status
                const newStatus = entry.new_data?.status
                const changedBy = entry.changed_by_name || 'System'
                return (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 py-2 border-b border-white/5 last:border-0"
                  >
                    <div className="w-1.5 h-1.5 rounded-full bg-white/20 mt-1.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-medium text-gray-300">{changedBy}</span>
                        {entry.action === 'INSERT' && (
                          <span className="text-xs text-gray-500">
                            created mapping as{' '}
                            <span className="text-gray-400">{newStatus?.replace('_', ' ')}</span>
                          </span>
                        )}
                        {entry.action === 'UPDATE' && (
                          <span className="text-xs text-gray-500">
                            {oldStatus && newStatus && oldStatus !== newStatus ? (
                              <>
                                changed{' '}
                                <span className="text-gray-400">{oldStatus.replace('_', ' ')}</span>
                                {' → '}
                                <span className="text-gray-400">{newStatus.replace('_', ' ')}</span>
                                {newStatus === 'edited' && entry.new_data?.edited_reasoning && <> with new reasoning</>}
                              </>
                            ) : 'updated this mapping'}
                          </span>
                        )}
                        {entry.action === 'DELETE' && (
                          <span className="text-xs text-gray-500">deleted this mapping</span>
                        )}
                      </div>
                      <p className="text-[11px] text-gray-600 mt-0.5">
                        {new Date(entry.changed_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── Comments Tab ── */}
      {activeTab === 'comments' && (
        <div>
          {commentsLoading ? (
            <p className="text-xs text-gray-500 py-4 text-center">Loading comments...</p>
          ) : comments.length === 0 ? (
            <p className="text-xs text-gray-600 py-2 text-center">
              No comments yet. Be the first.
            </p>
          ) : (
            <div className="space-y-2 mb-3">
              {comments.map(comment => (
                <div key={comment.id} className="bg-white/5 border border-white/10 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-gray-300">
                      {comment.author_name || 'Unknown'}
                    </span>
                    <span className="text-[11px] text-gray-600">
                      {new Date(comment.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-xs sm:text-sm text-gray-400">{comment.comment}</p>
                </div>
              ))}
            </div>
          )}

          <div className="mt-3">
            <textarea
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              placeholder="Leave a comment..."
              className="
                w-full bg-white/5 border border-white/10 rounded-lg
                px-3 py-2 text-xs sm:text-sm text-gray-300 placeholder-gray-600
                focus:outline-none focus:border-blue-500/50 focus:bg-white/10
                resize-none transition
              "
              rows={2}
            />
            <button
              onClick={submitComment}
              disabled={submittingComment || !newComment.trim()}
              className="
                mt-2 px-3 py-1.5 rounded-md
                text-[11px] sm:text-xs font-medium
                text-gray-300 bg-white/5 border border-white/10
                hover:bg-white/10 hover:border-white/20
                disabled:opacity-40 disabled:cursor-not-allowed
                active:scale-[0.98] transition
              "
            >
              {submittingComment ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
